import { graphHooks } from '@tunasong/graph-lib/react'
import type { PollOption } from '@tunasong/schemas'
import { EntitySelect } from '@tunasong/tunasong-ui'
import { Button, Grid, TextField, Typography } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { serviceName } from './audio-candidates.js'
import { getOptionLabel } from './option-label.js'
import css from './properties.module.css'

/**
 * Created once per editor and shared by every row, so a row's props only change when its own option does.
 * They act on the latest poll, never on the one from the render they were handed out in. GH #1969, #1966
 */
export interface OptionRowHandlers {
  rename(idx: number, name: string): void
  /** `name` is set when the ref is cleared, so the option does not end up blank. */
  relink(idx: number, entityRef: string | undefined, name?: string): void
  /** `label` is what the row shows, for the confirm text. */
  remove(idx: number, label: string): void
  /** Open "Find audio" for this option alone. */
  findAudio(idx: number): void
}

export interface OptionRowProps {
  idx: number
  option: PollOption
  answerCount: number
  handlers: OptionRowHandlers
}

/**
 * One editable text option. A linked option keeps its text field: `name` is the option's label and its primary
 * text, with the linked entity shown under it; the placeholder shows what an empty label falls back to. Editing
 * it never touches the linked entity.
 */
export const OptionRow: FC<OptionRowProps> = props => {
  const { idx, option, answerCount, handlers } = props
  const { entity } = graphHooks.useEntity(option.entityRef)
  const isLinked = Boolean(option.entityRef)

  const handleClearRef = () =>
    handlers.relink(idx, undefined, option.name.trim() ? option.name : (entity?.name ?? option.name))

  return (
    <Grid container>
      <Grid size={{ xs: 3 }}>
        <EntitySelect
          buttonText="Link Entity"
          entityId={option.entityRef}
          onSelected={ref => handlers.relink(idx, ref)}
        />
      </Grid>
      <Grid size={{ xs: 4 }} className={css.optionGridFlex}>
        <TextField
          /** Uncontrolled: remount when the link changes so a fallback name written on Clear Ref shows up. */
          key={option.entityRef ?? 'unlinked'}
          className={css.optionTextField}
          placeholder={isLinked ? (entity?.name ?? 'Label (optional)') : 'Option'}
          defaultValue={option.name}
          onChange={ev => handlers.rename(idx, ev.target.value)}
          variant="standard"
          disabled={answerCount > 0}
        />
        {isLinked ? (
          <Typography variant="caption" color="primary">
            Linked
          </Typography>
        ) : null}
        {option.audio ? (
          <Typography variant="caption" color="textSecondary">
            Audio: {option.audio.kind === 'entity' ? 'Hosted audio' : serviceName(option.audio.service)}
          </Typography>
        ) : null}
        {isLinked ? (
          <Button
            size="small"
            aria-label={`Find audio for ${getOptionLabel(option, entity?.name)}`}
            onClick={() => handlers.findAudio(idx)}
          >
            Find audio
          </Button>
        ) : null}
      </Grid>
      <Grid size={{ xs: 2 }}>
        {isLinked && answerCount === 0 ? <Button onClick={handleClearRef}>Clear Ref</Button> : null}
      </Grid>
      <Grid size={{ xs: 2 }}>
        <Button onClick={() => handlers.remove(idx, getOptionLabel(option, entity?.name))}>Delete</Button>
      </Grid>
      {answerCount > 0 ? (
        <Typography variant="caption" color="textSecondary" className={css.helpText}>
          {answerCount === 1 ? '1 answer' : `${answerCount} answers`}
        </Typography>
      ) : null}
    </Grid>
  )
}

export default OptionRow
